import hljs from 'highlight.js';
import { useEffect } from 'react';
import { Avatar, Box, Button, Card, Flex, Grid, Group, Loader, Text } from '@mantine/core';
import ReactMarkdown from 'react-markdown';
import ChatGptImg from '../../../logo_small.png';
import MyImg from '../img/me.png';

import { IconFile, IconFileSpreadsheet, IconFileText, IconFileTypePdf, IconFileWord } from '@tabler/icons-react';
import './PromptResponseList.css';

// The agent emits <question visual="Label">Instruction</question> follow-ups and
// <productfeed .../> tags. react-markdown strips raw HTML, so without this they would
// vanish (questions) or leak as raw text (product feeds). Pull questions out into
// clickable chips and drop product-feed tags the admin UI can't render as a carousel.
// The visual="…" label is optional — a bare <question>Instruction</question> falls back
// to using the instruction itself as the chip label.
const QUESTION_RE = /<question(?:\s+visual="([^"]*)")?\s*>([\s\S]*?)<\/question>/gi;
// Match both the self-closing <productfeed … /> form and a paired <productfeed>…</productfeed>.
const PRODUCT_FEED_RE = /<productfeed\b[\s\S]*?(?:\/>|<\/productfeed>)/gi;
// During streaming a tag can arrive half-formed (e.g. `<question visual="Yes">Show m…`)
// before its closing tag streams in. Strip any trailing, not-yet-closed question/product-feed
// tag so the raw fragment never flashes inside the bubble.
const PARTIAL_TAG_RE = /<(?:question|productfeed)\b[\s\S]*$/i;

const parseResponse = (raw) => {
  const questions = [];
  const text = (raw || '')
    .replace(QUESTION_RE, (_match, visual, prompt) => {
      const instruction = (prompt || '').trim();
      questions.push({ visual: (visual || instruction).trim(), prompt: instruction });
      return '';
    })
    .replace(PRODUCT_FEED_RE, '')
    .replace(PARTIAL_TAG_RE, '')
    .trim();

  return { text, questions };
};

const markdownComponents = {
  code({ className, children }) {
    return <code className={className}>{children}</code>;
  },
  // Make every link the agent returns (e.g. "[view it here](...)" for a created asset)
  // a real clickable anchor that opens in a new tab so the chat stays open.
  a({ href, children }) {
    return (
      <a href={href} target="_blank" rel="noreferrer">
        {children}
      </a>
    );
  },
};

const ResponseContent = ({ response, onQuickReply }) => {
  const { text: body, questions } = parseResponse(response);

  return (
    <>
      {body && (
        <div className="rlv-msg-md">
          <ReactMarkdown components={markdownComponents}>{body}</ReactMarkdown>
        </div>
      )}
      {onQuickReply && questions.length > 0 && (
        <Group gap="xs" mt="xs">
          {questions.map((question, index) => (
            <Button
              key={`${question.visual}-${index}`}
              variant="light"
              size="xs"
              radius="xl"
              onClick={() => onQuickReply(question.prompt)}
            >
              {question.visual || question.prompt}
            </Button>
          ))}
        </Group>
      )}
    </>
  );
};

const PromptResponseList = ({ responseList, isLoading, streamState, type, onQuickReply }) => {
  useEffect(() => {
    const timeout = setTimeout(() => {
      hljs.highlightAll();
    }, 1000);

    return () => {
      clearTimeout(timeout);
    };
  });

  const getFileIconByUrl = (fileUrl) => {
    const fileUrlArray = fileUrl.split('.');
    const fileExtension = fileUrlArray[fileUrlArray.length - 1];

    if (!fileExtension) {
      return <IconFile size={14} />;
    }

    if (fileExtension === 'pdf') {
      return <IconFileTypePdf size={14} />;
    }

    if (fileExtension === 'txt') {
      return <IconFileText size={14} />;
    }

    if (fileExtension.startsWith('xls')) {
      return <IconFileSpreadsheet size={14} />;
    }

    if (fileExtension.startsWith('doc')) {
      return <IconFileWord size={14} />;
    }

    return <IconFile size={14} />;
  };

  return (
    <Box className="prompt-response-list">
      {type === 'chat'
        ? responseList.map((responseData, index) => (
            <Card
              className="response-container"
              mb="xs"
              style={{ border: 'none', boxShadow: 'none' }}
              key={responseData.id}
            >
              <Card.Section inheritPadding py="sm">
                <Grid>
                  <Grid.Col span={{ base: 12, md: 1 }}>
                    <img className="avatar-image" src={responseData.selfFlag ? MyImg : ChatGptImg} alt="avatar" />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 11 }} className={responseData.error ? 'error-response ' : 'response-data-container'}>
                    {responseData.image && <img src={responseData.image} className="ai-image" alt="generated ai" />}

                    {responseData.response && (
                      // Only the latest message keeps its follow-up chips live; chips on
                      // earlier turns would re-submit a now out-of-context instruction.
                      <ResponseContent
                        response={responseData.response}
                        onQuickReply={index === responseList.length - 1 ? onQuickReply : undefined}
                      />
                    )}

                    {responseData.file && (
                      <a href={responseData.file.url} target="_blank" className="chat-file-icon" rel="noreferrer">
                        {getFileIconByUrl(responseData.file.url)}

                        {responseData.file.name}
                      </a>
                    )}
                  </Grid.Col>
                </Grid>
              </Card.Section>
            </Card>
          ))
        : responseList.map((responseData, index) => (
            <Box key={responseData.id} mb="sm">
              <Flex
                direction={responseData.selfFlag ? 'row-reverse' : 'row'}
                align="flex-end"
                gap="xs"
              >
                {!responseData.selfFlag && (
                  <Avatar
                    src={ChatGptImg}
                    alt="Lucy"
                    size="sm"
                    radius="xl"
                    className="lucy-avatar"
                    imageProps={{ style: { objectFit: 'contain', padding: 3 } }}
                    style={{ flexShrink: 0 }}
                  />
                )}
                <Box
                  px="sm"
                  py={8}
                  style={{
                    borderRadius: '12px',
                    maxWidth: '82%',
                    lineHeight: 1.45,
                    backgroundColor: responseData.selfFlag
                      ? 'var(--mantine-color-gray-1)'
                      : 'var(--mantine-color-primary-0)',
                    border: responseData.selfFlag ? 'none' : '1px solid var(--mantine-color-primary-1)',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                  }}
                >
                  {responseData.error ? (
                    <Text c="red">{responseData.response}</Text>
                  ) : (
                    <>
                      {responseData.image && <img src={responseData.image} className="ai-image" alt="generated ai" />}
                      {responseData.response && (
                        // Only the latest message keeps its follow-up chips live; chips on
                        // earlier turns would re-submit a now out-of-context instruction.
                        <ResponseContent
                          response={responseData.response}
                          onQuickReply={index === responseList.length - 1 ? onQuickReply : undefined}
                        />
                      )}
                    </>
                  )}
                </Box>
              </Flex>
            </Box>
          ))}
      {(isLoading || streamState) && (
        <Flex align="center" gap="xs" px="sm" py="xs">
          <Loader size="sm" type="dots" />
          {streamState && <Text size="sm" c="dimmed" fs="italic">{streamState}</Text>}
        </Flex>
      )}
    </Box>
  );
};

export default PromptResponseList;
